import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  NodeCard,
  NodeListItem,
  NodeAssociations,
  NodePropertyBadge,
  NodeProgress,
  NodeUsers,
  NodeDetailPage,
} from '@/components/nodes'
import { projectService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import type { Project } from '@/types'
import { Users, FileText, MessageSquare, ListTodo, Boxes } from 'lucide-react'
import { projectDetailTabs } from '@/lib/config/tabs'
import type { NodeDetailConfig } from '@/components/nodes/NodeDetailPage'

export default function ProjectDetailPage() {
  const { user } = useAuth()

  const projectConfig: NodeDetailConfig<Project> = {
    nodeType: 'project',
    service: {
      getById: async (id: string) => {
        return await projectService.getByIdWithMessages(id)
      },
      delete: async (id: string) => {
        await projectService.delete(id)
      },
      updateStatus: async (id: string, status) => {
        await projectService.updateStatus(id, status)
      },
    },
    tabs: projectDetailTabs,
    getEditUrl: (id: string) => `/projects/${id}/edit`,
    getListUrl: '/projects',
    getMembersUrl: (id: string) => `/projects/${id}/members`,
    canEdit: (project: Project, user) => {
      if (!user || !project) return false
      return user.role === 'SuperAdmin' || project.creator.id === user.id
    },
    canDelete: (project: Project, user) => {
      if (!user || !project) return false
      return user.role === 'SuperAdmin' || project.creator.id === user.id
    },
    getMetadata: (project: Project) => (
      <>
        <NodeAssociations
          nodes={[project.workspace]}
          nodeType="workspaces"
          maxVisible={1}
          showViewAll={false}
          icon={Boxes}
        />
        <NodePropertyBadge type="status" value={project.status} />
        <NodeProgress items={project.tasks} />
        <NodeUsers
          users={project.users}
          maxVisible={5}
          avatarSize="md"
          viewAllUrl={`/projects/${project.id}/members`}
          viewAllLabel="View all"
          icon={Users}
        />
      </>
    ),
    getTabContent: (project: Project, tab: string) => {
      switch (tab) {
        case 'tasks':
          return (
            <NodeCard
              title="Project Tasks"
              description={`${project.tasks.length} total`}
              icon={<ListTodo className="h-5 w-5" />}
              items={project.tasks}
              context="task"
              config={{
                showStatus: true,
                showPriority: true,
                showHorizon: false,
                showType: false,
                showDescription: true,
                showWorkspace: false,
                showAssignee: true,
                showDueDate: true,
                showSubtasks: true,
                statusSize: 'sm',
                emptyState: 'No tasks in this project yet',
              }}
            />
          )
        case 'messages':
          return (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Messages
                </CardTitle>
                <CardDescription>
                  {project.messages.length} messages about this project
                </CardDescription>
              </CardHeader>
              <CardContent>
                {project.messages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-4">No messages yet</p>
                    <Button asChild size="sm">
                      <Link to="/messages">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        View All Messages
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {project.messages.map((message) => (
                      <NodeListItem
                        key={message.id}
                        item={message}
                        context="message"
                        config={{
                          showDate: true,
                          showReplyCount: true,
                          showReadStatus: true,
                          showMessageType: true,
                          currentUserId: user?.id,
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )
        case 'documents':
          return (
            <NodeCard
              title="Documents"
              description={`${project.documents.length} documents in this project`}
              icon={<FileText className="h-5 w-5" />}
              items={project.documents}
              context="document"
              config={{
                showType: true,
                showTypeIcon: true,
                showDescription: true,
                showWorkspace: false,
                showCreator: true,
                showCreatorName: true,
                showDate: true,
                showSharedCount: false,
                emptyState: 'No documents yet',
              }}
            />
          )
        default:
          return null
      }
    },
    getBadges: (project: Project) => ({
      tasks: project.tasks.length,
      messages: project.messages.length,
      documents: project.documents.length,
    }),
    loadingText: 'Loading project...',
    notFoundText: 'Project not found',
    backToListText: 'Back to Projects',
  }

  return <NodeDetailPage config={projectConfig} />
}
